/**
 * Turn AF Phase 1 — Wingman Chat page.
 *
 * ChatGPT/Claude-style single-column conversation between :
 *   - user       (aligned right, subtle accent bubble)
 *   - assistant  (Hail Mary GPT-4o, full width, markdown)
 *   - wingman    (Wingman WhatsApp inbound, full width, green badge)
 *   - system     (dim italic)
 *
 * Isolated from the existing orchestrator chat (which lives elsewhere).
 * Talks only to /api/wingman-chat/* — no shared state.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MessageSquare, Send, Paperclip, Loader2, Bot, User, Sparkles, Phone } from "lucide-react";
import { API, authHeaders } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const DEFAULT_SESSION_ID = "00000000-0000-0000-0000-000000000001";
const SESSION_KEY = "hm.wingman_chat.session_id";

function getSessionId() {
  try {
    const stored = window.localStorage.getItem(SESSION_KEY);
    if (stored) return stored;
  } catch (_) { /* ignore */ }
  return DEFAULT_SESSION_ID;
}
function setSessionId(sid) {
  try { window.localStorage.setItem(SESSION_KEY, sid); } catch (_) {}
}

/* ------------------------------------------------------------------- */
/* Message row — one per role                                          */
/* ------------------------------------------------------------------- */
function RoleAvatar({ role }) {
  if (role === "user") {
    return (
      <div className="h-7 w-7 rounded-full bg-hm-accent-soft border border-hm-border-strong flex items-center justify-center text-hm-accent shrink-0">
        <User className="h-3.5 w-3.5" />
      </div>
    );
  }
  if (role === "wingman") {
    return (
      <div className="h-7 w-7 rounded-full bg-emerald-950 border border-emerald-700 flex items-center justify-center text-emerald-300 shrink-0">
        <MessageSquare className="h-3.5 w-3.5" />
      </div>
    );
  }
  if (role === "system") {
    return (
      <div className="h-7 w-7 rounded-full bg-hm-bg-1 border border-hm-border flex items-center justify-center text-hm-fg-subtle shrink-0">
        <Sparkles className="h-3.5 w-3.5" />
      </div>
    );
  }
  return (
    <div className="h-7 w-7 rounded-full bg-hm-bg-2 border border-hm-border-strong flex items-center justify-center text-hm-accent shrink-0">
      <Bot className="h-3.5 w-3.5" />
    </div>
  );
}

function AttachmentPill({ att }) {
  const label = att.filename || "attachment";
  const url = att.storage_url || att.upstream_url;
  return (
    <a
      href={url || "#"}
      target={url ? "_blank" : undefined}
      rel="noreferrer"
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-1 rounded border border-hm-border bg-hm-bg-2 text-[11px] mono text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong transition-colors",
        !url && "opacity-60 cursor-not-allowed",
      )}
      data-testid="wingman-chat-attachment"
    >
      <Paperclip className="h-3 w-3" />
      <span className="truncate max-w-[220px]">{label}</span>
      {att.size_bytes ? (
        <span className="text-hm-fg-subtle">
          {(att.size_bytes / 1024).toFixed(1)} KB
        </span>
      ) : null}
    </a>
  );
}

function MessageRow({ message }) {
  const { role, content, content_type, attachments, created_at } = message;
  const ts = useMemo(() => {
    if (!created_at) return "";
    try {
      return new Date(created_at).toLocaleString(undefined, {
        hour: "2-digit", minute: "2-digit",
        month: "short", day: "2-digit",
      });
    } catch (_) { return ""; }
  }, [created_at]);

  const isUser = role === "user";

  return (
    <div
      className={cn("flex gap-3 py-4", isUser && "justify-end")}
      data-testid={`chat-message-${role}`}
    >
      {!isUser && <RoleAvatar role={role} />}
      <div className={cn(
        "min-w-0",
        isUser ? "max-w-[75%]" : "flex-1",
      )}>
        <div className="flex items-baseline gap-2 mb-1">
          <span className={cn(
            "text-xs font-medium",
            role === "user" && "text-hm-fg-muted",
            role === "assistant" && "text-hm-fg",
            role === "wingman" && "text-emerald-300",
            role === "system" && "text-hm-fg-subtle italic",
          )}>
            {role === "user" && "You"}
            {role === "assistant" && "Hail Mary"}
            {role === "wingman" && "Wingman (WhatsApp)"}
            {role === "system" && "System"}
          </span>
          <span className="mono text-[10px] text-hm-fg-subtle">{ts}</span>
        </div>

        <div
          className={cn(
            "prose prose-invert prose-sm max-w-none",
            isUser && "bg-hm-bg-1 border border-hm-border rounded-lg px-3 py-2 inline-block",
            role === "system" && "text-hm-fg-subtle italic",
          )}
        >
          {content_type === "markdown" || role === "assistant" ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ""}</ReactMarkdown>
          ) : (
            <p className="whitespace-pre-wrap m-0 text-hm-fg">{content}</p>
          )}
        </div>

        {attachments && attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {attachments.map((a, i) => <AttachmentPill key={i} att={a} />)}
          </div>
        )}
      </div>
      {isUser && <RoleAvatar role={role} />}
    </div>
  );
}

/* ------------------------------------------------------------------- */
/* Streaming SSE reader                                                */
/* ------------------------------------------------------------------- */
async function* readSseEvents(response) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) return;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      if (!frame.trim() || frame.startsWith(":")) continue;
      let event = "message"; let data = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      try {
        yield { event, data: data ? JSON.parse(data) : null };
      } catch (_) {
        yield { event, data: { raw: data } };
      }
    }
  }
}

/* ------------------------------------------------------------------- */
/* Main page                                                           */
/* ------------------------------------------------------------------- */
export default function WingmanChat() {
  const [sessionId, setSid] = useState(() => getSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState("");
  const [connected, setConnected] = useState(false);

  // Turn BA — outbound WhatsApp target. Persisted in localStorage so the
  // operator doesn't retype their number every visit. Format: E.164
  // digits only, optional leading '+' (server strips it).
  const [waTarget, setWaTarget] = useState(
    () => (typeof window !== "undefined"
      ? (localStorage.getItem("hm.wingman_chat.whatsapp_target") || "")
      : "")
  );
  const [waSending, setWaSending] = useState(false);
  const saveWaTarget = useCallback((v) => {
    const cleaned = String(v || "").trim();
    setWaTarget(cleaned);
    try { localStorage.setItem("hm.wingman_chat.whatsapp_target", cleaned); }
    catch (_) { /* localStorage may be blocked */ }
  }, []);

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const eventSourceRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  /* Load initial history */
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(
          `${API}/wingman-chat/messages?session_id=${sessionId}&limit=50`,
          { credentials: "include", headers: authHeaders() },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        if (!cancel) {
          setMessages(j.data || []);
          scrollToBottom();
        }
      } catch (err) {
        if (!cancel) toast.error(`Failed to load messages: ${err.message}`);
      }
    })();
    return () => { cancel = true; };
  }, [sessionId, scrollToBottom]);

  /* Live SSE subscription for inbound Wingman + own turns */
  useEffect(() => {
    const url = `${API}/wingman-chat/live?session_id=${sessionId}`;
    const es = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = es;
    es.addEventListener("ready", () => setConnected(true));
    es.addEventListener("message", (ev) => {
      try {
        const m = JSON.parse(ev.data);
        // Ignore user + assistant echoes that are already tracked
        // through the streaming POST response (avoid duplicates); we
        // only splice-in the 'wingman' inbound ones here.
        if (m.role !== "wingman") return;
        setMessages((prev) => {
          if (prev.some((x) => x.id === m.id)) return prev;
          return [...prev, m];
        });
        scrollToBottom();
      } catch (_) { /* ignore */ }
    });
    es.onerror = () => setConnected(false);
    return () => { es.close(); };
  }, [sessionId, scrollToBottom]);

  /* Auto-grow textarea */
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 220)}px`;
  }, [input]);

  const handleSend = useCallback(async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setSending(true);
    setInput("");
    const optimisticUserId = `optimistic-${Date.now()}`;
    setMessages((prev) => [...prev, {
      id: optimisticUserId, role: "user", content: msg,
      content_type: "text", attachments: [],
      created_at: new Date().toISOString(),
    }]);
    scrollToBottom();

    try {
      const res = await fetch(`${API}/wingman-chat/completions`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      let assembled = "";
      let assistantIdReal = null;
      let userIdReal = null;

      for await (const { event, data } of readSseEvents(res)) {
        if (event === "start") {
          userIdReal = data?.user_message_id;
          // Replace optimistic user id with real one
          setMessages((prev) => prev.map((m) =>
            m.id === optimisticUserId ? { ...m, id: userIdReal } : m
          ));
        } else if (event === "token") {
          assembled += data?.content || "";
          setStreaming(assembled);
          scrollToBottom();
        } else if (event === "done") {
          assistantIdReal = data?.assistant_message_id;
          setMessages((prev) => [...prev, {
            id: assistantIdReal, role: "assistant",
            content: assembled, content_type: "markdown",
            attachments: [],
            created_at: new Date().toISOString(),
          }]);
          setStreaming("");
          scrollToBottom();
        } else if (event === "error") {
          toast.error(`LLM error: ${data?.detail || "unknown"}`);
        }
      }
    } catch (err) {
      toast.error(`Send failed: ${err.message}`);
    } finally {
      setSending(false);
    }
  }, [input, sending, sessionId, scrollToBottom]);

  /* Turn BA — outbound WhatsApp send.
     Sends the composer text to /api/wingman-chat/send-to-whatsapp
     which relays it via Meta Cloud API and persists the row. The /live
     SSE broadcast will fold the outbound message into `messages` (the
     current SSE handler filters to role='wingman' only — outbound needs
     an explicit optimistic append below). */
  const handleSendToWhatsApp = useCallback(async () => {
    const msg = input.trim();
    const to = (waTarget || "").trim();
    if (!msg || waSending) return;
    if (!/^\+?\d{6,20}$/.test(to)) {
      toast.error("Enter a valid WhatsApp number (E.164, digits only)");
      return;
    }
    setWaSending(true);
    setInput("");
    const optimisticId = `wa-optimistic-${Date.now()}`;
    setMessages((prev) => [...prev, {
      id: optimisticId, role: "user", content: msg,
      content_type: "text", attachments: [],
      metadata: { direction: "outbound", channel: "whatsapp", destination: to },
      created_at: new Date().toISOString(),
    }]);
    scrollToBottom();
    try {
      const res = await fetch(`${API}/wingman-chat/send-to-whatsapp`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ to, text: msg, session_id: sessionId }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        const d = j?.detail;
        const dmsg = typeof d === "string"
          ? d
          : (d?.meta_message || d?.reason || `HTTP ${res.status}`);
        throw new Error(dmsg);
      }
      const j = await res.json();
      setMessages((prev) => prev.map((m) =>
        m.id === optimisticId ? { ...m, id: j.message_id } : m
      ));
      toast.success("Sent to WhatsApp");
    } catch (err) {
      // Roll back the optimistic message so the user isn't misled.
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      toast.error(`WhatsApp send failed: ${err.message}`);
    } finally {
      setWaSending(false);
    }
  }, [input, waTarget, waSending, sessionId, scrollToBottom]);


  const onKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  const newSession = () => {
    const sid = crypto.randomUUID();
    setSessionId(sid);
    setSid(sid);
    setMessages([]);
    setStreaming("");
    toast.success("New chat session started");
  };

  return (
    <div
      className="flex flex-col h-full bg-hm-bg text-hm-fg"
      data-testid="wingman-chat-page"
    >
      {/* Header */}
      <div className="h-14 flex items-center justify-between px-6 border-b border-hm-border shrink-0">
        <div className="flex items-center gap-3">
          <MessageSquare className="h-4 w-4 text-hm-accent" />
          <div className="flex flex-col leading-none">
            <span className="text-hm-fg font-semibold text-sm">Wingman Chat</span>
            <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mt-1">
              Session · {sessionId.slice(0, 8)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest",
              connected ? "text-emerald-400" : "text-hm-fg-subtle",
            )}
            data-testid="wingman-chat-live-status"
          >
            <span className={cn(
              "h-1.5 w-1.5 rounded-full",
              connected ? "bg-emerald-400" : "bg-hm-fg-subtle",
            )} />
            {connected ? "Live" : "Offline"}
          </span>
          <button
            onClick={newSession}
            className="mono text-[10px] uppercase tracking-widest text-hm-fg-muted hover:text-hm-fg px-3 py-1.5 rounded border border-hm-border hover:border-hm-border-strong transition-colors"
            data-testid="wingman-chat-new-session"
          >
            New session
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto"
        data-testid="wingman-chat-messages"
      >
        <div className="max-w-[820px] mx-auto px-6">
          {messages.length === 0 && !streaming && (
            <div className="py-24 text-center text-hm-fg-subtle">
              <MessageSquare className="h-10 w-10 mx-auto text-hm-fg-subtle mb-3" />
              <p className="text-sm">
                No messages yet. Ask the Hail Mary assistant about your briefs,
                sources, or Wingman inbounds.
              </p>
              <p className="mono text-[11px] text-hm-fg-subtle mt-2">
                <kbd className="px-1.5 py-0.5 rounded border border-hm-border">Ctrl+Enter</kbd> to send
              </p>
            </div>
          )}

          {messages.map((m) => <MessageRow key={m.id} message={m} />)}

          {streaming && (
            <div className="flex gap-3 py-4" data-testid="chat-streaming-row">
              <RoleAvatar role="assistant" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xs font-medium text-hm-fg">Hail Mary</span>
                  <Loader2 className="h-3 w-3 text-hm-accent animate-spin" />
                </div>
                <div className="prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{streaming}</ReactMarkdown>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-hm-border shrink-0 bg-hm-bg">
        <div className="max-w-[820px] mx-auto px-6 py-4">
          {/* Turn BA — outbound WhatsApp destination bar. Small, discrete,
              persisted in localStorage. Empty state prompts operator. */}
          <div className="flex items-center gap-2 mb-2">
            <Phone className="h-3.5 w-3.5 text-hm-fg-subtle shrink-0" aria-hidden />
            <input
              type="tel"
              value={waTarget}
              onChange={(e) => saveWaTarget(e.target.value)}
              placeholder="WhatsApp target · e.g. +33612345678"
              className="flex-1 bg-hm-bg-1 border border-hm-border rounded-md px-2.5 py-1 text-xs text-hm-fg placeholder:text-hm-fg-subtle outline-none focus:border-hm-border-strong transition-colors mono"
              data-testid="wingman-wa-target-input"
              autoComplete="tel"
              inputMode="tel"
            />
            <button
              type="button"
              onClick={handleSendToWhatsApp}
              disabled={!input.trim() || waSending || !waTarget.trim()}
              className={cn(
                "shrink-0 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] transition-colors mono",
                input.trim() && waTarget.trim() && !waSending
                  ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/20"
                  : "bg-hm-bg-2 text-hm-fg-subtle border border-hm-border cursor-not-allowed"
              )}
              data-testid="wingman-send-to-whatsapp-btn"
              title="Send composer text to the WhatsApp target above"
            >
              {waSending
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Phone className="h-3 w-3" />}
              Send to WhatsApp
            </button>
          </div>

          <div className="flex items-end gap-3 rounded-lg border border-hm-border bg-hm-bg-1 focus-within:border-hm-border-strong transition-colors">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Ask about briefs, sources, Wingman messages…"
              className="flex-1 bg-transparent outline-none px-4 py-3 text-sm text-hm-fg placeholder:text-hm-fg-subtle resize-none min-h-[44px] max-h-[220px]"
              data-testid="wingman-chat-input"
              disabled={sending}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              className={cn(
                "shrink-0 mx-2 mb-2 h-8 w-8 flex items-center justify-center rounded-md transition-colors",
                input.trim() && !sending
                  ? "bg-hm-accent text-hm-bg hover:opacity-90"
                  : "bg-hm-bg-2 text-hm-fg-subtle cursor-not-allowed",
              )}
              data-testid="wingman-chat-send"
              aria-label="Send message"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
          <p className="mono text-[10px] text-hm-fg-subtle mt-2 text-center">
            <kbd className="px-1 py-0.5 rounded border border-hm-border">Ctrl+Enter</kbd> to send · GPT-4o via Emergent LLM Key
          </p>
        </div>
      </div>
    </div>
  );
}
